import React from 'react';

/**
 * Single source of truth for date/time rendering across the app.
 *
 * Renders a semantic `<time>` element with the right `dateTime` attribute
 * and a `title` tooltip showing the full ISO timestamp on hover. The
 * `format` prop picks one of the common text representations — pick the
 * one that matches the surface, don't reach for `toLocaleDateString`
 * directly anywhere else.
 *
 * ```tsx
 * <Time value={iso} />                    // "5 minutes ago"
 * <Time value={iso} format="short" />     // "May 2, 2026"
 * <Time value={iso} format="medium" />    // "May 2, 2026" (month-long)
 * <Time value={iso} format="long" />      // "May 2, 2026, 3:42 PM"
 * <Time value={iso} format="verbose" />   // "Friday, May 2, 2026"
 * <Time value={iso} format="compact" />   // "Sat May 02, 2026" (friendly, fixed width)
 * <Time value={iso} format="iso" />       // "2026-05-02" (machine, sortable)
 * ```
 *
 * For non-JSX callers (string composition, toast messages, etc.), use
 * the exported `formatTime(value, format)` helper — same vocabulary.
 *
 * Adding a new format: extend the `TimeFormat` union and `FORMATTERS`.
 * Don't define one-off date utilities elsewhere.
 */

export type TimeFormat = 'relative' | 'short' | 'medium' | 'long' | 'verbose' | 'compact' | 'iso';

export interface TimeProps {
  /** Anything `new Date(...)` accepts. */
  value: string | number | Date;
  /** Visible text format. Default: `'relative'`. */
  format?: TimeFormat;
  /** Pass-through for layout / typography classes on the rendered `<time>`. */
  className?: string;
}

const formatRelative = (d: Date): string => {
  const ms = Date.now() - d.getTime();
  // Future timestamps (trial ends, renewal dates) phrase as "in X …",
  // past timestamps as "X … ago" — same thresholds either direction.
  const future = ms < 0;
  const phrase = (n: number, unit: string): string => {
    const text = `${n} ${unit}${n === 1 ? '' : 's'}`;
    return future ? `in ${text}` : `${text} ago`;
  };
  const sec = Math.round(Math.abs(ms) / 1000);
  if (sec < 45) return 'just now';
  const min = Math.round(sec / 60);
  if (min < 60) return phrase(min, 'minute');
  const hr = Math.round(min / 60);
  if (hr < 24) return phrase(hr, 'hour');
  const day = Math.round(hr / 24);
  if (day < 30) return phrase(day, 'day');
  const mo = Math.round(day / 30);
  if (mo < 12) return phrase(mo, 'month');
  const yr = Math.round(mo / 12);
  return phrase(yr, 'year');
};

const FORMATTERS: Record<TimeFormat, (d: Date) => string> = {
  relative: formatRelative,
  short: (d) =>
    d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }),
  medium: (d) =>
    d.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }),
  long: (d) =>
    d.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    }),
  verbose: (d) =>
    d.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    }),
  // Friendly fixed-width: "Sat May 02, 2026". Weekday-short (3) + space +
  // month-short (3) + space + 2-digit day (2) + ", " + 4-digit year =
  // 16 chars regardless of value. Use for tabular layouts where every
  // row's date column must line up but ISO would feel too machine-y.
  compact: (d) => {
    const weekday = d.toLocaleDateString('en-US', { weekday: 'short' });
    const month = d.toLocaleDateString('en-US', { month: 'short' });
    const day = String(d.getDate()).padStart(2, '0');
    const year = d.getFullYear();
    return `${weekday} ${month} ${day}, ${year}`;
  },
  // Fixed-width ISO short date (YYYY-MM-DD). Always 10 chars regardless of
  // value — locale-independent, sortable lexically. Use for machine-facing
  // surfaces; prefer `compact` for human timelines. Built from local date
  // components so it agrees with `short`/`compact` (toISOString is UTC and
  // can disagree by a day).
  iso: (d) => {
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  },
};

const toDate = (value: string | number | Date): Date =>
  value instanceof Date ? value : new Date(value);

/**
 * Pure string formatter. Use when you need the formatted text inline
 * (toast messages, composed strings) rather than a JSX `<time>` element.
 * Does NOT carry the ISO tooltip — prefer `<Time>` whenever you're
 * rendering text into the DOM.
 */
export const formatTime = (
  value: string | number | Date,
  format: TimeFormat = 'short',
): string => FORMATTERS[format](toDate(value));

export const Time: React.FC<TimeProps> = ({ value, format = 'relative', className }) => {
  const date = toDate(value);
  const iso = date.toISOString();
  return (
    <time dateTime={iso} title={iso} className={className}>
      {FORMATTERS[format](date)}
    </time>
  );
};

export default Time;
